import { useState, useRef, useEffect, useMemo } from 'react';
import { Box, TextField, IconButton, Typography, Paper, Tooltip, Chip } from '@mui/material';
import { TypingIndicator } from './TypingIndicator';
import { Send, Paperclip, X } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useNode } from '@lingaai/shared-hooks';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { useCurrentUser } from '@lingaai/user-management';
import ReactMarkdown from 'react-markdown';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { ChartRenderer } from './ChartRenderer';
import { ChatNodeForm } from './ChatNodeForm';
import { PicklistManagerForm } from './PicklistManagerForm';
import { CrmImportForm } from './CrmImportForm';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ChartAction {
  type: 'show_chart';
  chart_type: 'bar' | 'line' | 'pie';
  data: Array<{ label: string; value: number }>;
  title: string;
}

interface FormAction {
  type: 'show_form';
  form_id: string;
  data: {
    nodeType?: string;
    prefill?: Record<string, unknown>;
    picklistName?: string;
  };
}

type Action = ChartAction | FormAction;

interface EntityReference {
  id: string;
  type: string;
  name: string;
}

interface Message {
  role: 'user' | 'agent';
  content: string;
  timestamp: Date;
  actions?: Action[];
  entities?: EntityReference[];
}

// Map route prefixes to node types
const ROUTE_TO_NODE_TYPE: Record<string, string> = {
  organizations: 'Organization',
  people: 'Person',
  contacts: 'Person',
  deals: 'Deal',
  opportunities: 'Opportunity',
  vehicles: 'Vehicle',
  fundraising: 'FundraisingOpportunity',
  leads: 'Lead',
  campaigns: 'Campaign',
  activities: 'Activity',
  tasks: 'Task',
  documents: 'Document',
};

// Map node types to their display name field
const NODE_DISPLAY_FIELD: Record<string, string> = {
  Activity: 'subject',
  Task: 'title',
};

// Map node types to route prefixes
const NODE_TYPE_TO_ROUTE: Record<string, string> = {
  Organization: 'organizations',
  Person: 'people',
  Deal: 'deals',
  Opportunity: 'opportunities',
  Vehicle: 'vehicles',
  FundraisingOpportunity: 'fundraising',
  Campaign: 'campaigns',
  Event: 'events',
  Activity: 'activities',
  Task: 'tasks',
  Document: 'documents',
  Investment: 'investments',
  ExternalFund: 'external-funds',
  Commitment: 'commitments',
  InvestmentAccount: 'investment-accounts',
  CapitalCall: 'capital-calls',
  Distribution: 'distributions',
  Report: 'reports',
};

interface ChatPanelProps {
  onConversationIdChange?: (id: string | null) => void;
  loadConversationId?: string | null;
  resetKey?: number;
  onMessagesChange?: (messages: Message[]) => void;
}

export function ChatPanel({ onConversationIdChange, loadConversationId, resetKey }: ChatPanelProps = {}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [completedForms, setCompletedForms] = useState<Set<string>>(new Set());
  const [contextActive, setContextActive] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showImportForm, setShowImportForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { data: user } = useCurrentUser();

  // Parse context from route
  const contextInfo = useMemo(() => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    const routePrefix = parts[0];
    const nodeId = parts[1];
    const nodeType = ROUTE_TO_NODE_TYPE[routePrefix || ''];
    if (!nodeType || !nodeId || nodeId.length < 10) return null;
    return { nodeType, nodeId, routePrefix };
  }, [location.pathname]);

  // Fetch node details for context
  const { data: contextNode } = useNode(contextInfo?.nodeId);

  // Clear context - disable it without navigating
  const clearContext = () => {
    setContextActive(false);
  };

  // Re-enable context when route changes
  useEffect(() => {
    setContextActive(true);
  }, [location.pathname]);

  // Load conversation history when loadConversationId changes
  useEffect(() => {
    if (loadConversationId && user?.id) {
      fetch(`${API_URL}/crm/chat/conversations/${loadConversationId}/history?user_id=${user.id}`)
        .then((res) => res.json())
        .then((history) => {
          // Parse history into UI messages
          const uiMessages: Message[] = history
            .filter((item: any) => item.type === 'message')
            .map((item: any) => {
              const content = extractMessageContent(item);

              // For assistant messages, parse out ---ACTIONS--- marker
              if (item.role === 'assistant') {
                const actionsMarkerIndex = content.indexOf('---ACTIONS---');
                if (actionsMarkerIndex !== -1) {
                  const textBeforeMarker = content.substring(0, actionsMarkerIndex).trim();
                  const jsonAfterMarker = content.substring(actionsMarkerIndex + 13).trim();

                  try {
                    const parsed = JSON.parse(jsonAfterMarker);
                    return {
                      role: 'agent' as const,
                      content: textBeforeMarker,
                      timestamp: new Date(),
                      entities: parsed.entities || [],
                      actions: parsed.actions || [],
                    };
                  } catch {
                    // If parsing fails, return content as-is
                    return {
                      role: 'agent' as const,
                      content,
                      timestamp: new Date(),
                    };
                  }
                }
              }

              return {
                role: item.role === 'user' ? ('user' as const) : ('agent' as const),
                content,
                timestamp: new Date(),
              };
            });
          setMessages(uiMessages);
          setConversationId(loadConversationId);
        })
        .catch((error) => console.error('Failed to load conversation history:', error));
    } else if (loadConversationId === null) {
      // New conversation - clear messages
      setMessages([]);
      setConversationId(null);
    }
  }, [loadConversationId, resetKey, user?.id]);

  // Notify parent when conversationId changes
  useEffect(() => {
    if (onConversationIdChange) {
      onConversationIdChange(conversationId);
    }
  }, [conversationId, onConversationIdChange]);

  // Helper to extract message content from AgentInputItem
  const extractMessageContent = (item: any): string => {
    if (item.role === 'user') {
      if (typeof item.content === 'string') {
        const match = item.content.match(/Message: (.+)$/s);
        return match ? match[1] : item.content;
      }
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'input_text');
        if (textPart?.text) {
          const match = textPart.text.match(/Message: (.+)$/s);
          return match ? match[1] : textPart.text;
        }
        return textPart?.text || '';
      }
    } else if (item.role === 'assistant') {
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'output_text');
        if (textPart?.text) {
          try {
            const parsed = JSON.parse(textPart.text.replace(/^```(?:json)?\s*\n?/m, '').replace(/\n?```\s*$/m, ''));
            return parsed.response || textPart.text;
          } catch {
            return textPart.text;
          }
        }
      }
    }
    return '';
  };

  // Handle form completion - add confirmation message and hide form
  const handleFormSuccess = (node: unknown, message: string, formKey: string) => {
    // Mark form as completed so it hides
    setCompletedForms((prev) => new Set(prev).add(formKey));

    // Add confirmation message with link (if node exists)
    const nodeData = node as { id: string; type: string } | null;
    const nodeConfig = nodeData ? NODE_TYPE_CONFIG[nodeData.type] : null;
    const pluralPath = nodeConfig?.displayNamePlural.toLowerCase() || `${nodeData?.type.toLowerCase()}s`;
    const confirmMessage: Message = {
      role: 'agent',
      content: nodeData ? `✓ ${message}. [View ${nodeData.type}](/${pluralPath}/${nodeData.id})` : `✓ ${message}`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, confirmMessage]);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Parse CRM context from current route
  const getCRMContext = () => {
    // If context is disabled, don't send entity context
    if (!contextActive) {
      const path = location.pathname;
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 0) return { currentView: 'dashboard' };
      const view = parts[0] || 'dashboard';
      return { currentView: parts.length === 1 ? `${view} list` : `${view} detail` };
    }

    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);

    if (parts.length === 0) return { currentView: 'dashboard' };

    const view = parts[0] || 'dashboard';
    if (parts.length === 1) {
      return { currentView: `${view} list` };
    }

    // Detail view: /organizations/:id
    return {
      currentView: `${view} detail`,
      entityType: view.endsWith('s') ? view.slice(0, -1) : view,
      entityId: parts[1],
    };
  };

  const handleSend = async () => {
    if (!input.trim() || !user || !user.tenant_id) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Add placeholder for streaming response
    const streamingMessage: Message = {
      role: 'agent',
      content: '',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, streamingMessage]);

    try {
      const response = await fetch(`${API_URL}/crm/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          tenant_id: user.tenant_id,
          user_id: user.id,
          user_name: user.full_name || `${user.first_name} ${user.last_name}`,
          conversation_id: conversationId,
          crm_context: getCRMContext(),
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'token') {
                accumulatedText += data.content;
                // Update streaming message with accumulated text
                setMessages((prev) => prev.map((msg, idx) => (idx === prev.length - 1 ? { ...msg, content: accumulatedText } : msg)));
              } else if (data.type === 'done') {
                // Final message with entities and actions
                setMessages((prev) =>
                  prev.map((msg, idx) =>
                    idx === prev.length - 1
                      ? {
                          ...msg,
                          content: accumulatedText || data.response,
                          entities: data.entities || [],
                          actions: data.actions || [],
                        }
                      : msg
                  )
                );
                setConversationId(data.conversation_id);
                setIsLoading(false);
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      // Replace streaming message with error
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === prev.length - 1
            ? {
                ...msg,
                content: 'Sorry, I encountered an error. Please try again.',
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get context display name based on node type
  const contextDisplayName = useMemo(() => {
    if (!contextNode?.props || !contextInfo?.nodeType) return undefined;
    const field = NODE_DISPLAY_FIELD[contextInfo.nodeType] || 'name';
    return contextNode.props[field] as string | undefined;
  }, [contextNode, contextInfo]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      setUploadedFile(file);
      setShowImportForm(true);

      // Add user message about file upload
      const userMessage: Message = {
        role: 'user',
        content: `Upload CSV file: ${file.name}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
    } else {
      // Add error message
      const errorMessage: Message = {
        role: 'agent',
        content: 'Please upload a CSV file (.csv extension required)',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle import success
  const handleImportSuccess = (result: { inserted: number; skipped: number; message: string }) => {
    setShowImportForm(false);
    setUploadedFile(null);

    const confirmMessage: Message = {
      role: 'agent',
      content: `✓ ${result.message}${result.skipped > 0 ? `\n\nSkipped ${result.skipped} duplicate(s) to avoid conflicts.` : ''}`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, confirmMessage]);
  };

  // Handle import cancel
  const handleImportCancel = () => {
    setShowImportForm(false);
    setUploadedFile(null);

    const cancelMessage: Message = {
      role: 'agent',
      content: 'Import cancelled.',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, cancelMessage]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Context indicator */}
      {contextInfo && contextNode && contextActive && (
        <Box
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Context:
          </Typography>
          <Chip size="small" label={`${contextInfo.nodeType}: ${contextDisplayName}`} onDelete={clearContext} deleteIcon={<X size={14} />} sx={{ maxWidth: 250 }} />
        </Box>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography variant="body2">Ask me anything about your CRM data.</Typography>
          </Box>
        )}

        {messages.map((msg, idx) => (
          <Box key={idx} sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Box
              sx={{
                width: 4,
                bgcolor: msg.role === 'user' ? 'primary.main' : 'success.main',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {msg.role === 'user' ? 'You' : 'CRM Manager'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
              {msg.role === 'agent' && idx === messages.length - 1 && isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <TypingIndicator isTyping={isLoading} />
                  <Typography variant="body2" color="text.secondary">
                    Linga is thinking...
                  </Typography>
                </Box>
              )}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: msg.role === 'user' ? 'action.hover' : 'action.selected',
                  borderRadius: 2,
                }}
              >
                <Box>
                  {msg.role === 'user' ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                  ) : (
                    <Box
                      sx={{
                        '& p': { m: 0 },
                        '& a': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </Box>
                  )}
                  {msg.entities && msg.entities.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Related:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {msg.entities.map((entity, entityIdx) => {
                          const route = NODE_TYPE_TO_ROUTE[entity.type];
                          return route ? (
                            <Link key={entityIdx} to={`/${route}/${entity.id}`} style={{ textDecoration: 'none' }}>
                              <Chip label={entity.name} size="small" clickable sx={{ fontSize: '0.75rem' }} />
                            </Link>
                          ) : null;
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
              {msg.actions?.map((action, actionIdx) => {
                if (action.type === 'show_chart') {
                  return <ChartRenderer key={actionIdx} action={action} />;
                }
                if (action.type === 'show_form') {
                  const formKey = `${idx}-${actionIdx}`;
                  // Hide form if already completed
                  if (completedForms.has(formKey)) {
                    return null;
                  }

                  // Handle picklist manager form
                  if (action.form_id === 'manage_picklist' && action.data?.picklistName) {
                    return <PicklistManagerForm key={actionIdx} picklistName={action.data.picklistName} onSuccess={() => handleFormSuccess(null, 'Picklist updated', formKey)} />;
                  }

                  // Handle node creation form
                  if (action.data?.nodeType) {
                    return <ChatNodeForm key={actionIdx} nodeType={action.data.nodeType} prefill={action.data.prefill} onSuccess={(node, message) => handleFormSuccess(node, message, formKey)} />;
                  }
                }
                return null;
              })}
            </Box>
          </Box>
        ))}

        {/* CSV Import Form */}
        {showImportForm && uploadedFile && (
          <Box sx={{ mb: 2 }}>
            <CrmImportForm file={uploadedFile} onSuccess={handleImportSuccess} onCancel={handleImportCancel} />
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Upload CSV">
            <IconButton size="small" disabled={isLoading} onClick={() => fileInputRef.current?.click()}>
              <Paperclip size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Cmd/Ctrl+Enter adds new line
              if (e.key === 'Enter') {
                if (e.metaKey || e.ctrlKey) {
                  // Add new line
                  setInput((prev) => prev + '\n');
                  e.preventDefault();
                } else if (!e.shiftKey) {
                  // Send message
                  e.preventDefault();
                  handleSend();
                }
              }
            }}
            placeholder="Ask about your CRM data... (Cmd+Enter for new line)"
            disabled={isLoading}
            size="small"
          />
          <Tooltip title="Send message">
            <span>
              <IconButton onClick={handleSend} disabled={!input.trim() || isLoading} color="primary">
                <Send size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
